'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { cn } from '@medizzconnect/shared';
import { VideoCard } from './video-card';
import { VideoCardSkeleton } from './video-card-skeleton';
import { useVideos, type VideosPage } from './use-videos';

interface VideoGridProps {
  initialData: VideosPage;
  className?: string;
  onSelectVideo?: (profileId: string) => void;
}

/**
 * Gibt die aktuelle Spaltenanzahl basierend auf Fensterbreite zurück.
 * Mobile (<640px): 1 Spalte (TikTok-Style, Portrait-Videos).
 * Stimmt exakt mit den CSS-Breakpoints des Grids überein.
 */
function useColumnCount(): number {
  const [cols, setCols] = useState(5); // SSR-Default: 5 (= 25 Videos, stimmt mit Server-Fetch überein)

  useEffect(() => {
    const calc = () => {
      const w = window.innerWidth;
      if (w >= 1280) return 5;
      if (w >= 768)  return 4;
      if (w >= 640)  return 3;
      return 1; // Mobile: 1 Spalte für TikTok-Style-Scroll
    };
    setCols(calc());
    const onResize = () => setCols(calc());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return cols;
}

// Mobile (1 Spalte): 8 Videos pro Seite – genug zum Scrollen ohne zu viel laden
// Desktop (3-5 Spalten): immer 5 Zeilen
const ROWS_PER_PAGE = 5;

/**
 * Video-Grid mit Pagination.
 * Mobile:   1 Spalte, 8 Videos, Portrait-Ratio 3:4, Scroll-to-Play.
 * Desktop:  3–5 Spalten, 15–25 Videos, Landscape-Ratio 5:4, Hover-to-Play.
 */
export function VideoGrid({ initialData, className, onSelectVideo }: VideoGridProps) {
  const t = useTranslations('home');
  const cols = useColumnCount();
  // Mobile: 8 Videos/Seite, Desktop: cols × 5
  const perPage = cols === 1 ? 8 : cols * ROWS_PER_PAGE; // 8 | 15 | 20 | 25

  const {
    profiles,
    isFetching,
    isLoading,
    isError,
    mockClickEnabled,
    currentPage,
    navigatePage,
    totalPages,
  } = useVideos(initialData, perPage);
  const visiblePageCount = Math.max(1, totalPages);

  // Grid-Breakpoints: Mobile 1 Spalte (TikTok), Desktop 3–5 Spalten
  const gridCss = `
    .video-grid-dynamic {
      grid-template-columns: repeat(1, 1fr);
    }
    @media (min-width: 640px) {
      .video-grid-dynamic { grid-template-columns: repeat(3, 1fr); }
    }
    @media (min-width: 768px) {
      .video-grid-dynamic { grid-template-columns: repeat(4, 1fr); }
    }
    @media (min-width: 1280px) {
      .video-grid-dynamic { grid-template-columns: repeat(5, 1fr); }
    }
  `;

  // Endlos-Navigation: wrap-around + neues Shuffle pro Klick
  const handlePageChange = (direction: 'prev' | 'next') => {
    const nextPage =
      direction === 'next'
        ? currentPage >= visiblePageCount ? 1 : currentPage + 1
        : currentPage <= 1 ? visiblePageCount : currentPage - 1;
    navigatePage(nextPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Ladezustand (erste Seite, kein initialData vorhanden)
  if (isLoading) {
    return (
      <section className={cn('w-full px-0 pb-16', className)}>
        <style dangerouslySetInnerHTML={{ __html: gridCss }} />
        <div className="grid video-grid-dynamic">
          {Array.from({ length: perPage }).map((_, i) => (
            <VideoCardSkeleton key={i} />
          ))}
        </div>
      </section>
    );
  }

  // Fehlerzustand
  if (isError) {
    return (
      <section className={cn('w-full px-0 pb-16', className)}>
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <svg
            className="mb-4 h-12 w-12 text-error-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
            />
          </svg>
          <p className="text-sm text-text-secondary">{t('videos.noResults')}</p>
        </div>
      </section>
    );
  }

  // Leerer Zustand
  if (profiles.length === 0) {
    return (
      <section className={cn('w-full px-0 pb-16', className)}>
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <svg
            className="mb-4 h-16 w-16 text-text-muted"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
          <h3 className="text-base font-medium text-text-primary">
            {t('videos.noResults')}
          </h3>
          <p className="mt-1 text-sm text-text-muted">
            {t('videos.noResultsDescription')}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className={cn('w-full px-0 pb-16', className)}>
      <style dangerouslySetInnerHTML={{ __html: gridCss }} />

      {/* Video-Grid (leichtes Dimming beim Seiten-Laden) */}
      <div
        className={cn(
          'grid video-grid-dynamic transition-opacity duration-200',
          isFetching && 'opacity-60 pointer-events-none',
        )}
      >
        {profiles.map((profile, index) => (
          <VideoCard
            key={profile.id}
            profile={profile}
            priority={index < 6}
            onSelect={onSelectVideo}
            mockClickEnabled={mockClickEnabled}
          />
        ))}
      </div>

      {/* Remix-Navigation: nur ← → (endlos, wrap-around, kein Seitenzähler) */}
      {profiles.length > 0 && (
        <div className="mt-8 flex items-center justify-center gap-6">
          {/* Zurück */}
          <button
            type="button"
            onClick={() => handlePageChange('prev')}
            disabled={isFetching}
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-border-default bg-white text-text-secondary transition-colors hover:bg-bg-subtle hover:border-primary-400 disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Vorherige Seite"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          {/* Vor */}
          <button
            type="button"
            onClick={() => handlePageChange('next')}
            disabled={isFetching}
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-border-default bg-white text-text-secondary transition-colors hover:bg-bg-subtle hover:border-primary-400 disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Nächste Seite"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      )}
    </section>
  );
}
